import { useEffect, useCallback } from "react";

export interface TermsConditionsModalData {
  title: string;
  date: string;
  description: {
    type: "heading" | "subheading" | "paragraph" | "list";
    content: string | string[];
  }[];
}

interface TermsConditionsModalProps {
  data: TermsConditionsModalData | null;
  onClose: () => void;
}

export default function TermsConditionsModal({
  data,
  onClose,
}: TermsConditionsModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!data) return;

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [data, handleKeyDown]);

  if (!data) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-[600px] max-h-[85vh] bg-background-50 rounded-2xl overflow-hidden shadow-2xl animate-scale-in">
        
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-background-50/90 border border-background-200/60 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
          aria-label="Close modal"
        >
          <i className="ri-close-line text-foreground-700 w-4 h-4 flex items-center justify-center" />
        </button>


        {/* Header */}
        <div className="relative h-44 overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,oklch(var(--primary-700)/0.9),oklch(var(--background-800)/0.95))]" />

          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent" />

          <div className="absolute bottom-6 left-6">
            <span className="inline-flex px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-background-50/90 border border-background-200/40 text-foreground-700 mb-2">
              Legal
            </span>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
              {data.title}
            </h2>

            <p className="text-sm text-foreground-600 mt-1">
              {data.date}
            </p>
          </div>
        </div>


        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-11rem)]">

          <div className="space-y-5 text-sm md:text-sm text-foreground-600 leading-relaxed mb-8">

            {data.description.map((block, index) => {

              if (block.type === "heading") {
                return (
                  <h4
                    key={index}
                    className="text-sm font-semibold text-foreground-950 mt-2"
                  >
                    {block.content as string}
                  </h4>
                );
              }


              if (block.type === "subheading") {
                return (
                  <h5
                    key={index}
                    className="text-sm font-semibold text-foreground-900"
                  >
                    {block.content as string}
                  </h5>
                );
              }


              if (block.type === "list") {
                return (
                  <ul
                    key={index}
                    className="list-disc pl-5 space-y-2"
                  >
                    {(block.content as string[]).map((item, i) => (
                      <li key={i}>
                        {item}
                      </li>
                    ))}
                  </ul>
                );
              }


              return (
                <p
                  key={index}
                  className="whitespace-pre-line"
                >
                  {block.content as string}
                </p>
              );

            })}

          </div>


          {/* CTA */}
          <a
            href="#cta"
            onClick={(e) => {
              e.preventDefault();
              onClose();

              setTimeout(() => {
                document
                  .querySelector("#cta")
                  ?.scrollIntoView({
                    behavior: "smooth",
                  });
              }, 200);
            }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer"
          >
            Contact Us
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </a>

        </div>

      </div>
    </div>
  );
}